import React, { useCallback, useState } from 'react'
import isEqual from 'react-fast-compare'
import Button from 'components/ui/button'
import Collapsible from 'components/ui/collapsible'
type Props = {
  children: React.ReactNode
  limit: number
  renderButton?: (button: React.ReactNode) => React.ReactNode
}

/**
 * A component that displays a limited number of items with a toggle to show/hide additional items
 * @param children - The list items to display
 * @param limit - The maximum number of items to show initially
 * @param renderButton - Optional function to customize the show more/less button rendering
 * @returns A list that shows a limited number of items with a toggle button for the rest
 */
const ExpandableList = ({ children, limit, renderButton }: Props) => {
  const items = React.Children.toArray(children)
  const hiddenItems = items.splice(limit)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const toggleCollapsible = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded)
  }, [])
  const button = (
    <Button
      appearance="inline-link"
      caption={isExpanded ? 'Show less' : 'Show more'}
      compact
      onClick={toggleCollapsible}
    />
  )
  return (
    <>
      {items}
      {hiddenItems.length > 0 && (
        <>
          <Collapsible open={isExpanded}>{hiddenItems}</Collapsible>
          {renderButton ? renderButton(button) : button}
        </>
      )}
    </>
  )
}

export default React.memo<Props>(ExpandableList, isEqual)
